import {
  listLegacyCommandCenterSurfaces,
  validateLegacyMigrationRegistry,
} from '@/governor/system-workspaces/LegacyCommandCenterMigrationRegistry';

function stateClass(
  state: string,
): string {
  if (
    state === 'certified' ||
    state === 'retired'
  ) {
    return 'bg-emerald-100 text-emerald-900';
  }

  if (state === 'projected') {
    return 'bg-sky-100 text-sky-900';
  }

  if (state === 'mapped') {
    return 'bg-violet-100 text-violet-900';
  }

  return 'bg-slate-100 text-slate-800';
}

export default function LegacyCommandCenterMigrationInventory() {
  const surfaces =
    listLegacyCommandCenterSurfaces();

  const validation =
    validateLegacyMigrationRegistry();

  const systemIds = [
    ...new Set(
      surfaces.map(
        surface =>
          surface.owningSystemId,
      ),
    ),
  ];

  const retiredCount =
    surfaces.filter(
      surface =>
        surface.migrationState ===
        'retired',
    ).length;

  return (
    <section className="my-6 rounded-[2rem] border border-amber-200 bg-amber-50 p-5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-800">
            CC-SYS-001G
          </p>

          <h2 className="mt-2 text-3xl font-bold text-slate-950">
            Legacy Command Center Migration
          </h2>

          <p className="mt-2 max-w-3xl text-slate-700">
            Existing panels remain online while each surface is
            assigned to one governed system owner. Evidence and
            history are preserved until replacement projections are
            certified.
          </p>
        </div>

        <span
          className={
            'rounded-full px-4 py-2 text-sm font-bold ' +
            (
              validation.valid
                ? 'bg-emerald-100 text-emerald-900'
                : 'bg-rose-100 text-rose-900'
            )
          }
        >
          {validation.valid
            ? 'REGISTRY VALID'
            : 'REGISTRY BLOCKED'}
        </span>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Legacy surfaces
          </p>
          <p className="mt-1 text-2xl font-bold">
            {surfaces.length}
          </p>
        </div>

        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Governed owners
          </p>
          <p className="mt-1 text-2xl font-bold">
            {systemIds.length}
          </p>
        </div>

        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Duplicate IDs
          </p>
          <p className="mt-1 text-2xl font-bold">
            {validation.duplicateIds.length}
          </p>
        </div>

        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Retired
          </p>
          <p className="mt-1 text-2xl font-bold">
            {retiredCount}
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-amber-200 bg-white p-4 text-sm text-slate-700">
        <b>Current migration rule:</b> no legacy panel may be removed
        until its governed replacement preserves evidence, history,
        authority, and certification.
      </div>

      <div className="mt-5 grid gap-3 xl:grid-cols-2">
        {surfaces.map(surface => (
          <article
            key={surface.id}
            className="rounded-2xl border border-amber-200 bg-white p-4"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-bold text-slate-950">
                  {surface.title}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {surface.sourceReference}
                </p>
              </div>

              <span
                className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${stateClass(
                  surface.migrationState,
                )}`}
              >
                {surface.migrationState}
              </span>
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <p className="text-sm text-slate-700">
                <b>System:</b>{' '}
                {surface.owningSystemId}
              </p>

              <p className="text-sm text-slate-700">
                <b>Governor:</b>{' '}
                {surface.owningGovernor}
              </p>

              <p className="text-sm text-slate-700">
                <b>Legacy visible:</b>{' '}
                {String(
                  surface.legacyVisible,
                )}
              </p>

              <p className="text-sm text-slate-700">
                <b>Retirement blocked:</b>{' '}
                {String(
                  surface
                    .retirementBlockedUntilCertified,
                )}
              </p>
            </div>

            <p className="mt-3 text-sm text-slate-600">
              {surface.notes}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
